/* ==========================================================================
   ELEVATION — procedural graphics for Beans n Flavour
   Canvas graphics, all derived from real coffee data:
     1. Grind fields   — particle size distributions per brew method
     2. Bag artwork    — generated pack art when a lot has no photo
     3. Ghats ridgeline— estates plotted at true altitude
   Re-runs on shopify:section:load so the theme editor stays live.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function cssVar(name, fallback) {
    var v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  }

  /* HiDPI canvas sizing. Returns null when the canvas has no layout box yet. */
  function fit(cv) {
    var r = cv.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    var d = Math.min(window.devicePixelRatio || 1, 2);
    cv.width = Math.round(r.width * d);
    cv.height = Math.round(r.height * d);
    var ctx = cv.getContext('2d');
    ctx.setTransform(d, 0, 0, d, 0, 0);
    return { ctx: ctx, w: r.width, h: r.height };
  }

  /* Deterministic PRNG so graphics stay identical across redraws. */
  function prng(seed) {
    var s = seed * 9301 + 49297;
    return function () {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };
  }

  /* ====================================================== 1. GRIND FIELDS */
  function drawGrind(cv) {
    var f = fit(cv);
    if (!f) return;
    var ctx = f.ctx, w = f.w, h = f.h;
    var min = parseFloat(cv.dataset.min) || 1;
    var max = parseFloat(cv.dataset.max) || 3;
    var n = parseInt(cv.dataset.n, 10) || 500;
    var rnd = prng(parseInt(cv.dataset.seed, 10) || 3);

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = cssVar('--ev-surface-2', '#E9E2DA');
    ctx.fillRect(0, 0, w, h);

    var ink = cssVar('--ev-ink', '#2B1D16');
    var count = Math.round(n * (w * h) / (200 * 76));
    for (var i = 0; i < count; i++) {
      var r = min + rnd() * (max - min);
      var x = rnd() * w, y = rnd() * h;
      ctx.globalAlpha = 0.30 + rnd() * 0.45;
      ctx.fillStyle = ink;
      ctx.beginPath();
      var pts = 5 + Math.floor(rnd() * 3);
      for (var p = 0; p < pts; p++) {
        var ang = (p / pts) * 6.283, rr = r * (0.62 + rnd() * 0.62);
        var px = x + Math.cos(ang) * rr, py = y + Math.sin(ang) * rr;
        if (p === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  /* ====================================================== 2. BAG ARTWORK */
  function drawBag(cv) {
    var f = fit(cv);
    if (!f) return;
    var ctx = f.ctx, w = f.w, h = f.h;
    var hue = parseFloat(cv.dataset.hue) || 30;
    var alt = parseFloat(cv.dataset.alt) || 1200;
    var rnd = prng(parseInt(cv.dataset.seed, 10) || 11);

    ctx.clearRect(0, 0, w, h);

    var g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, 'hsl(' + hue + ' 26% 90%)');
    g.addColorStop(1, 'hsl(' + (hue + 18) + ' 20% 82%)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    /* contour bands — count derived from the lot's elevation */
    ctx.strokeStyle = 'rgba(43,29,22,.14)';
    ctx.lineWidth = 1;
    var bands = Math.max(3, Math.round(alt / 150));
    for (var b = 0; b < bands; b++) {
      ctx.beginPath();
      var baseY = h * (0.28 + b * 0.075);
      for (var x = 0; x <= w; x += 5) {
        var y = baseY
          + Math.sin((x / w) * 4.2 + b * 0.75 + hue * 0.02) * (11 - b * 0.5)
          + Math.sin((x / w) * 9.5 + b) * 3.5;
        if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    /* the bean */
    var R = Math.min(w, h) * 0.23;
    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.rotate(-0.34);
    ctx.fillStyle = 'rgba(43,29,22,.88)';
    ctx.beginPath();
    ctx.ellipse(0, 0, R * 0.74, R, 0, 0, 6.283);
    ctx.fill();
    ctx.strokeStyle = 'hsl(' + hue + ' 24% 88%)';
    ctx.lineWidth = Math.max(2, R * 0.13);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(0, -R * 0.8);
    ctx.quadraticCurveTo(R * 0.3, 0, 0, R * 0.8);
    ctx.stroke();
    ctx.restore();

    for (var i = 0; i < 40; i++) {
      ctx.globalAlpha = 0.05 + rnd() * 0.07;
      ctx.fillStyle = '#2B1D16';
      ctx.beginPath();
      ctx.arc(rnd() * w, rnd() * h, rnd() * 1.6 + 0.4, 0, 6.283);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  /* ====================================================== 3. GHATS RIDGELINE */
  function drawRidge(cv) {
    var f = fit(cv);
    if (!f) return;
    var ctx = f.ctx, w = f.w, h = f.h;

    var estates = [];
    try { estates = JSON.parse(cv.dataset.estates || '[]'); } catch (e) { estates = []; }

    var hill = cssVar('--ev-ink', '#2B1D16'),
        cherry = cssVar('--ev-cherry', '#C0392B'),
        muted = cssVar('--ev-muted', '#645850'),
        ink = cssVar('--ev-ink', '#2B1D16'),
        body = cssVar('--ev-body', 'sans-serif'),
        display = cssVar('--ev-display', 'sans-serif');

    var ground = cssVar('--ev-ground', '#F5EFE6');

    ctx.clearRect(0, 0, w, h);
    if (!estates.length) return;

    /* Scale to the estates actually on show, with headroom above the highest
       peak for its two lines of label. */
    var alts = estates.map(function (e) { return e.alt; });
    var hiAlt = Math.max.apply(null, alts);
    var loAlt = Math.min.apply(null, alts);
    var padT = 50, padB = 22;
    var ALTMIN = loAlt - 250, ALTMAX = hiAlt + 160;
    var Y = function (a) { return padT + (h - padT - padB) * (1 - (a - ALTMIN) / (ALTMAX - ALTMIN)); };

    var valley = ALTMIN + (ALTMAX - ALTMIN) * 0.1;
    var SIGMA = 0.1;

    /* Every estate raises its own hill, peaking at exactly its altitude, so a
       marker always lands on a crest rather than floating over empty sky. The
       spread is kept narrow enough that a tall neighbour's slope cannot swallow
       a shorter peak. */
    function profile(u, damp, shift) {
      var floor = valley + Math.sin(u * 5.1) * 16 + Math.sin(u * 11.7) * 7;
      var top = floor;
      for (var i = 0; i < estates.length; i++) {
        var d = (u - estates[i].at - shift) / SIGMA;
        var peak = floor + (estates[i].alt - floor) * damp;
        var v = floor + (peak - floor) * Math.exp(-(d * d) / 2);
        if (v > top) top = v;
      }
      return top;
    }

    function layer(damp, shift, alpha) {
      ctx.beginPath();
      ctx.moveTo(0, h);
      for (var x = 0; x <= w; x += 2) ctx.lineTo(x, Y(profile(x / w, damp, shift)));
      ctx.lineTo(w, h);
      ctx.closePath();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = hill;
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    /* An optional altitude window, drawn under the hills so it reads as a
       stratum of sky rather than a box on top of the terrain. Used to mark the
       threshold the copy is arguing about. */
    var bandLo = parseFloat(cv.dataset.bandLow),
        bandHi = parseFloat(cv.dataset.bandHigh),
        bandLabel = cv.dataset.bandLabel || '',
        bandY = null;

    if (isFinite(bandLo) && isFinite(bandHi) && bandHi > bandLo) {
      var yTop = Y(Math.min(bandHi, ALTMAX)),
          yBot = Y(Math.max(bandLo, ALTMIN));
      bandY = yTop;

      ctx.globalAlpha = 0.07;
      ctx.fillStyle = cherry;
      ctx.fillRect(0, yTop, w, yBot - yTop);
      ctx.globalAlpha = 0.4;
      ctx.strokeStyle = cherry;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(0, Math.round(yBot) + 0.5);
      ctx.lineTo(w, Math.round(yBot) + 0.5);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }

    /* No gridlines and no axis. The terrain is the scale, and every marker
       already carries its own altitude, so a ruled sky was saying it twice. */
    layer(0.70, -0.07, 0.18);
    layer(0.86, 0.05, 0.40);
    layer(1, 0, 0.95);

    /* The altitude the step currently in view is talking about. Drawn over the
       terrain rather than under it, because it is a callout, not scenery. */
    var focusAlt = parseFloat(cv.dataset.focusAlt),
        hasFocus = isFinite(focusAlt);

    if (hasFocus) {
      var fy = Math.round(Y(focusAlt)) + 0.5;
      ctx.strokeStyle = cherry;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(0, fy);
      ctx.lineTo(w, fy);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    /* Every label carries a halo in the page colour, so it stays readable
       whether it falls on sky or on the canopy behind it. */
    function label(text, x, y, fill, font, align) {
      ctx.font = font;
      ctx.textAlign = align;
      ctx.lineWidth = 3.5;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = ground;
      ctx.strokeText(text, x, y);
      ctx.fillStyle = fill;
      ctx.fillText(text, x, y);
    }

    ctx.textBaseline = 'middle';
    if (bandY !== null && bandLabel) {
      label(bandLabel, w - 10, bandY + 13, cherry, '600 11px ' + body, 'right');
    }

    /* Names take a step down on a phone-width canvas, where four of them share
       far less room. */
    var tight = w < 520;
    var nameFont = '600 ' + (tight ? 10.5 : 11.5) + 'px ' + display;
    var altFont = '500 ' + (tight ? 10 : 11) + 'px ' + body;

    ctx.textBaseline = 'alphabetic';
    estates.forEach(function (e) {
      var x = e.at * w, y = Y(e.alt);

      /* While a step is focused, estates that clear its line stay cherry and
         the rest recede, so the chart answers the step rather than restating
         itself. */
      var lit = !hasFocus || e.alt >= focusAlt;

      ctx.beginPath(); ctx.arc(x, y, 6, 0, 6.283);
      ctx.fillStyle = ground; ctx.fill();
      ctx.beginPath(); ctx.arc(x, y, lit ? 3.5 : 2.5, 0, 6.283);
      ctx.fillStyle = lit ? cherry : muted; ctx.fill();

      /* Nudge a label back inside the canvas rather than letting it clip. */
      ctx.font = nameFont;
      var half = ctx.measureText(e.name).width / 2 + 6;
      var lx = Math.max(half, Math.min(w - half, x));

      label(e.name, lx, y - (tight ? 21 : 24), lit ? ink : muted, nameFont, 'center');
      label(e.alt.toLocaleString('en-IN') + ' m', lx, y - 10, muted, altFont, 'center');
    });

    /* The focus caption goes on last, as a solid chip pinned to the left gutter
       the axis used to occupy. Halo text collided with whichever estate label
       shared its altitude; an opaque chip on top cannot. */
    if (hasFocus && cv.dataset.focusLabel) {
      var txt = cv.dataset.focusLabel,
          cy = Y(focusAlt),
          chipH = 20,
          padX = 8;

      ctx.font = '600 12px ' + body;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      var cw = ctx.measureText(txt).width + padX * 2;

      ctx.fillStyle = cherry;
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(10, cy - chipH / 2, cw, chipH, 3);
        ctx.fill();
      } else {
        ctx.fillRect(10, cy - chipH / 2, cw, chipH);
      }

      ctx.fillStyle = cssVar('--ev-on-cherry', '#FFFFFF');
      ctx.fillText(txt, 10 + padX, cy);
    }
  }

  /* ================================================== RIDGELINE SCROLLYTELLING
     The chart stays put while the steps scroll past it. Each step names an
     altitude; the chart annotates that line and dims whatever falls below it.
     Purely additive — with no steps, or no IntersectionObserver, the chart is
     exactly the static one it was before. */
  function initScrolly(root) {
    var cv = root.querySelector('[data-ev-ridge]');
    var steps = root.querySelectorAll('[data-ev-step]');
    if (!cv || !steps.length) return;

    function activate(el) {
      for (var i = 0; i < steps.length; i++) {
        steps[i].classList.toggle('is-active', steps[i] === el);
      }
      if (el.dataset.alt) {
        cv.dataset.focusAlt = el.dataset.alt;
        cv.dataset.focusLabel = el.dataset.focusLabel || '';
      } else {
        delete cv.dataset.focusAlt;
        delete cv.dataset.focusLabel;
      }
      drawRidge(cv);
    }

    if (!('IntersectionObserver' in window)) {
      for (var j = 0; j < steps.length; j++) steps[j].classList.add('is-active');
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) activate(en.target); });
    }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });

    for (var k = 0; k < steps.length; k++) io.observe(steps[k]);
    activate(steps[0]);
  }

  /* ====================================================== ROAST SCALE TABS */
  function initScale(root) {
    var steps = root.querySelectorAll('.ev-step');
    var out = root.querySelector('.ev-scale__out');
    if (!steps.length || !out) return;

    function select(i) {
      Array.prototype.forEach.call(steps, function (s, j) {
        s.setAttribute('aria-selected', j === i ? 'true' : 'false');
      });
      var s = steps[i];
      out.innerHTML =
        '<span class="ev-eyebrow">' + s.dataset.name + ' roast</span>' +
        '<h4>' + s.dataset.headline + '</h4>' +
        '<p>' + s.dataset.body + '</p>';
    }

    Array.prototype.forEach.call(steps, function (s, i) {
      s.addEventListener('click', function () { select(i); });
    });

    var start = 0;
    Array.prototype.forEach.call(steps, function (s, i) {
      if (s.getAttribute('aria-selected') === 'true') start = i;
    });
    select(start);
  }

  /* ====================================================== PACK FINDER */
  /* A swipeable rail of moods; each draws suggestions from the catalogue the
     section serialised into the page. A mood narrows the draw by product tag
     (`data-match`) and/or roast level (`data-roast`); one that sets neither
     draws at random from everything. Swiping the rail selects whichever mood
     settles under the centre, so touch needs no second tap. */
  function initFinder(root) {
    if (root.dataset.evReady) return;

    var poolEl = root.querySelector('[data-ev-pool]');
    var stage = root.querySelector('[data-ev-stage]');
    var brew = root.querySelector('[data-ev-brew]');
    var rail = root.querySelector('[data-ev-rail]');
    var prog = root.querySelector('[data-ev-prog]');
    var chips = root.querySelectorAll('[data-ev-mood]');
    if (!poolEl || !stage || !chips.length) return;

    var pool = [];
    try { pool = JSON.parse(poolEl.textContent) || []; } catch (e) { pool = []; }
    if (!pool.length) return;

    root.dataset.evReady = '1';

    var count = Math.min(parseInt(root.dataset.count, 10) || 3, pool.length);
    var flagTop = root.dataset.flagTop === 'true';
    var againHtml = (root.querySelector('[data-ev-again]') || {}).innerHTML || '';
    var roastHtml = (root.querySelector('[data-ev-roast-menu]') || {}).innerHTML || '';
    var current = null;
    var lastKey = '';
    var timer;

    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    function shuffle(list) {
      var a = list.slice();
      for (var i = a.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var t = a[i]; a[i] = a[j]; a[j] = t;
      }
      return a;
    }

    /* A mood matches on tags, on roast level, or on both. Anything it leaves
       blank simply is not tested, so a mood with nothing set matches all. */
    function matches(p, chip) {
      var raw = (chip.dataset.match || '').toLowerCase();
      var wanted = raw ? raw.split(',').map(function (t) { return t.trim(); }).filter(Boolean) : [];

      if (wanted.length) {
        var keys = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
        keys.push(String(p.cat || '').toLowerCase());
        if (!wanted.some(function (w) { return keys.indexOf(w) > -1; })) return false;
      }

      var band = (chip.dataset.roast || '').split('-');
      var lo = parseFloat(band[0]);
      if (!isNaN(lo)) {
        var hi = parseFloat(band.length > 1 ? band[1] : band[0]);
        if (isNaN(hi)) hi = lo;
        var r = Number(p.roast) || 0;
        if (r < lo || r > hi) return false;
      }
      return true;
    }

    /* Products matching the mood come first; the rest top the list up so a
       narrow mood can never return fewer cards than asked for. */
    function draw(chip, attempt) {
      var hits = [], rest = [];
      pool.forEach(function (p) { (matches(p, chip) ? hits : rest).push(p); });

      var picks = shuffle(hits).concat(shuffle(rest)).slice(0, count);
      var key = picks.map(function (p) { return p.url; }).join('|');

      /* Re-rolling should not hand back the same set twice in a row, but a mood
         narrow enough to match only `count` products has nothing else to offer —
         hence the attempt cap rather than looping until the set differs. */
      if (key === lastKey && (attempt || 0) < 4) return draw(chip, (attempt || 0) + 1);
      lastKey = key;
      return picks;
    }

    function card(p, i, why) {
      var top = flagTop && i === 0;
      var price = (p.varies ? 'From ' : '') + p.price;
      return '<article class="ev-find__card' + (top ? ' is-top' : '') + '" style="--i:' + i + '">' +
        '<a class="ev-find__card-link" href="' + esc(p.url) + '" aria-label="' + esc(p.title) + '"></a>' +
        (top ? '<span class="ev-find__flag">Closest match</span>' : '') +
        (p.img
          ? '<div class="ev-find__art"><img src="' + esc(p.img) + '" alt="' + esc(p.alt) + '" loading="lazy" width="520" height="520"></div>'
          : '') +
        '<div class="ev-find__meta">' +
          (why ? '<span class="ev-find__why">' + esc(why) + '</span>' : '') +
          '<span class="ev-find__name">' + esc(p.title) + '</span>' +
          (p.notes ? '<p class="ev-find__notes">' + esc(p.notes) + '</p>' : '') +
          '<div class="ev-roast"><span class="ev-eyebrow" style="font-size:.625rem;">Roast</span>' +
            '<span class="ev-roast__track">' +
              [1, 2, 3, 4, 5].map(function (n) {
                return '<span class="ev-roast__seg' + (n <= (p.roast || 3) ? ' is-on' : '') + '"></span>';
              }).join('') +
            '</span></div>' +
          '<div class="ev-find__foot"><span class="ev-price">' + esc(price) + '</span>' +
            '<span class="ev-find__go">View pack</span></div>' +
        '</div></article>';
    }

    function render(spec) {
      var picks = draw(spec);
      stage.innerHTML =
        '<div class="ev-find__verdict">' +
          '<span class="ev-eyebrow">Our pick for you</span>' +
          '<h3>' + esc(spec.dataset.headline) + '</h3>' +
          (spec.dataset.note ? '<p>' + esc(spec.dataset.note) + '</p>' : '') +
        '</div>' +
        '<div class="ev-find__cards">' +
          picks.map(function (p, i) { return card(p, i, spec.dataset.why); }).join('') +
        '</div>' +
        againHtml;

      var again = stage.querySelector('.ev-find__again');
      if (again) {
        again.addEventListener('click', function () {
          render(spec);
          var first = stage.querySelector('.ev-find__card-link');
          if (first) first.focus();
        });
      }
    }

    /* Brewing beat: long enough to read as deliberate, short enough not to nag. */
    function beat(fn) {
      clearTimeout(timer);
      if (reduce) { fn(); return; }
      stage.innerHTML = '';
      if (brew) brew.hidden = false;
      timer = setTimeout(function () {
        if (brew) brew.hidden = true;
        fn();
      }, 620);
    }

    /* The roast card is a question, not an answer, so it skips the brewing beat
       and puts the levels on the stage. Choosing one builds a spec of the same
       shape a mood chip has, which is all `draw` and `render` ever read. */
    function showRoasts() {
      clearTimeout(timer);
      if (brew) brew.hidden = true;
      stage.innerHTML = roastHtml;

      stage.querySelectorAll('[data-ev-roast]').forEach(function (b) {
        b.addEventListener('click', function () {
          stage.querySelectorAll('[data-ev-roast]').forEach(function (o) {
            o.classList.toggle('is-on', o === b);
          });
          lastKey = '';
          beat(function () {
            render({
              dataset: {
                headline: b.dataset.headline,
                note: b.dataset.note,
                why: b.dataset.why,
                match: '',
                roast: b.dataset.level
              }
            });
          });
        });
      });
    }

    function select(chip, centre, instant) {
      current = chip;
      Array.prototype.forEach.call(chips, function (c) {
        c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
      });
      if (centre && chip.scrollIntoView) {
        chip.scrollIntoView({
          inline: 'center',
          block: 'nearest',
          behavior: reduce ? 'auto' : 'smooth'
        });
      }

      if (chip.dataset.evRoastpick && roastHtml) { showRoasts(); return; }

      if (instant) { render(chip); return; }
      beat(function () { render(chip); });
    }

    Array.prototype.forEach.call(chips, function (c) {
      c.addEventListener('click', function () { select(c, true); });
    });

    /* An empty stage gives a shopper scrolling past no idea what the rail is
       for, so open on the first real mood with its cards already dealt. The
       roast card is skipped because it only asks another question. */
    if (root.dataset.preselect === 'true') {
      var opener = Array.prototype.filter.call(chips, function (c) {
        return !c.dataset.evRoastpick;
      })[0] || chips[0];
      select(opener, false, true);
    }

    /* -------------------------------------------------- rail behaviour */
    if (!rail) return;

    function overflow() { return rail.scrollWidth - rail.clientWidth; }

    function paintProgress() {
      if (!prog) return;
      var max = overflow();
      prog.hidden = max <= 4;
      var bar = prog.firstElementChild;
      if (bar) bar.style.width = (max <= 0 ? 100 : (rail.scrollLeft / max) * 100) + '%';
    }

    /* The mood sitting nearest the middle of the rail after a swipe settles. */
    function centred() {
      var mid = rail.scrollLeft + rail.clientWidth / 2;
      var best = null, bestGap = Infinity;
      Array.prototype.forEach.call(chips, function (c) {
        var gap = Math.abs(c.offsetLeft + c.offsetWidth / 2 - mid);
        if (gap < bestGap) { bestGap = gap; best = c; }
      });
      return best;
    }

    /* Only act when the swipe lands on a *different* mood, so the smooth scroll
       triggered by tapping a card cannot bounce back and re-roll its own result. */
    function settle() {
      var c = centred();
      if (c && c !== current) select(c, false);
    }

    var scrollTimer;
    rail.addEventListener('scroll', function () {
      paintProgress();
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(settle, 160);
    }, { passive: true });

    rail.addEventListener('keydown', function (e) {
      var dir = e.key === 'ArrowRight' ? 1 : (e.key === 'ArrowLeft' ? -1 : 0);
      if (!dir) return;
      e.preventDefault();
      var list = Array.prototype.slice.call(chips);
      var i = list.indexOf(current || centred());
      var next = list[Math.min(list.length - 1, Math.max(0, i + dir))];
      if (next) select(next, true);
    });

    root.querySelectorAll('[data-ev-nav]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = chips[0].offsetWidth + 12;
        rail.scrollBy({
          left: step * parseInt(btn.dataset.evNav, 10),
          behavior: reduce ? 'auto' : 'smooth'
        });
      });
    });

    paintProgress();
    window.addEventListener('resize', paintProgress);
  }

  /* ====================================================== BOOT */
  function paint(scope) {
    var root = scope || document;

    root.querySelectorAll('[data-ev-grind]').forEach(drawGrind);
    root.querySelectorAll('[data-ev-bag]').forEach(drawBag);
    root.querySelectorAll('[data-ev-ridge]').forEach(drawRidge);
    root.querySelectorAll('[data-ev-scrolly]').forEach(initScrolly);
    root.querySelectorAll('[data-ev-scale]').forEach(initScale);
    root.querySelectorAll('[data-ev-finder]').forEach(initFinder);
  }

  function repaintAll() {
    document.querySelectorAll('[data-ev-grind]').forEach(drawGrind);
    document.querySelectorAll('[data-ev-bag]').forEach(drawBag);
    document.querySelectorAll('[data-ev-ridge]').forEach(drawRidge);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { paint(); });
  } else {
    paint();
  }

  /* Canvas text is rasterised in whatever font is ready at the time, so a
     first paint can land in the fallback. Redraw once the webfonts are in. */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(repaintAll);
  }

  var rt;
  window.addEventListener('resize', function () {
    clearTimeout(rt);
    rt = setTimeout(repaintAll, 140);
  });

  /* Theme editor: sections are swapped in and out of the DOM live. */
  document.addEventListener('shopify:section:load', function (e) { paint(e.target); });
  document.addEventListener('shopify:section:unload', function () { clearTimeout(rt); });
})();
